import { Close as CloseIcon } from "@mui/icons-material";
import Logger from "../../../../services/Logger";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Rating,
    TextField,
    Typography
} from "@mui/material";
import React, { useState } from "react";
import { usePagesTranslation } from "../../../locales";

const FeedbackDialog = ({ open, onClose, onSubmit }) => {
    const { t } = usePagesTranslation();
    const [stars, setStars] = useState(0);
    const [difficultyDelta, setDifficultyDelta] = useState(null);
    const [comment, setComment] = useState("");

    const handleSubmit = () => {
        // Capture values before resetting state
        const feedbackData = {
            stars: stars > 0 ? stars : null,
            difficultyDelta: difficultyDelta !== null && difficultyDelta !== undefined ? difficultyDelta : null,
            comment: comment.trim() || null
        };
        
        Logger.log("FeedbackDialog - Submitting feedback:", feedbackData);
        
        onSubmit(feedbackData);
        handleClose();
    };

    const handleClose = () => {
        setStars(0);
        setDifficultyDelta(null);
        setComment("");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 2
                }
            }}
        >
            <DialogTitle sx={{
                pb: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="h6" component="div">
                    {t("puzzle.feedbackDialog.title")}
                </Typography>
                <Button
                    onClick={handleClose}
                    sx={{ minWidth: 'auto', p: 1 }}
                >
                    <CloseIcon />
                </Button>
            </DialogTitle>

            <DialogContent sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                    {/* Enjoyment - Star Rating */}
                    <Box>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                            {t("puzzle.feedbackDialog.enjoyment")}
                        </FormLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Rating
                                name="enjoyment-rating"
                                value={stars}
                                onChange={(_event, newValue) => {
                                    setStars(newValue || 0);
                                }}
                                size="large"
                            />
                            {stars > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {stars === 1
                                        ? t("puzzle.feedbackDialog.star-one", { count: stars })
                                        : t("puzzle.feedbackDialog.star-other", { count: stars })}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Difficulty */}
                    <Box>
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                                Difficulty
                            </FormLabel>
                            <RadioGroup
                                row
                                value={difficultyDelta !== null && difficultyDelta !== undefined ? difficultyDelta.toString() : ''}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    const numValue = value !== '' ? parseInt(value, 10) : null;
                                    Logger.log("FeedbackDialog - Difficulty changed:", numValue);
                                    setDifficultyDelta(numValue);
                                }}
                            >
                                <FormControlLabel value="-1" control={<Radio />} label={t("puzzle.feedbackDialog.difficulty-easier")} />
                                <FormControlLabel value="0" control={<Radio />} label={t("puzzle.feedbackDialog.difficulty-expected")} />
                                <FormControlLabel value="1" control={<Radio />} label={t("puzzle.feedbackDialog.difficulty-harder")} />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* Comment */}
                    <Box>
                        <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                            {t("puzzle.feedbackDialog.comment")}
                        </FormLabel>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={comment}
                            onChange={(e) => {
                                Logger.log("FeedbackDialog - Comment changed:", e.target.value);
                                setComment(e.target.value);
                            }}
                            placeholder={t("puzzle.feedbackDialog.comment-placeholder")}
                            variant="outlined"
                            maxLength={500}
                            helperText={t("puzzle.feedbackDialog.comment-max")}
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={handleClose} variant="outlined">
                    {t("btn.cancel")}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        backgroundColor: '#3BA666',
                        '&:hover': { backgroundColor: '#2e7d32' }
                    }}
                >
                    {t("btn.submit")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackDialog;

